'use client'

import React from 'react'
import Image from 'next/image'
import GradientSvgIcon from '@/components/GradientSvgIcon'
import { usesInsetProductImage } from '@/components/Refresh/SearchResults/searchResultsUtils'
import type { ServiceItem } from '@/types/interfaces/service-item'
import classes from './ProductImage.module.css'

const ASSET_BASE_URL = `https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}`

interface ProductImageProps {
    service: ServiceItem
}

function getImageAssetUrl(url: string) {
    return `${ASSET_BASE_URL}/${url}`
}

/**
 * Shelf product image well.
 * Matches search: visits/specialty full-bleed cover; lab/med inset on cream.
 */
export default function ProductImage({ service }: ProductImageProps) {
    const url = String(service.imageUrl ?? '')
    const isFallbackIcon = url.startsWith('fallback-icons/')
    const isPlaceholder = url.startsWith('fallback_images/')
    const usesInsetImage = usesInsetProductImage(service.category) || isPlaceholder || isFallbackIcon

    const wellClassName = [classes.well, usesInsetImage || !url ? classes.wellInset : classes.wellCover].join(' ')

    if (!url) {
        return <div className={wellClassName} aria-hidden />
    }

    if (isFallbackIcon) {
        return (
            <div className={wellClassName} aria-hidden>
                <GradientSvgIcon src={getImageAssetUrl(url)} size={72} />
            </div>
        )
    }

    if (usesInsetImage) {
        return (
            <div className={wellClassName}>
                <Image
                    src={getImageAssetUrl(url)}
                    alt=""
                    width={120}
                    height={120}
                    sizes="120px"
                    draggable={false}
                    className={classes.imageContain}
                    loading="lazy"
                    decoding="async"
                />
            </div>
        )
    }

    return (
        <div className={wellClassName}>
            <Image
                src={getImageAssetUrl(url)}
                alt=""
                fill
                sizes="(max-width: 48em) 40vw, 200px"
                draggable={false}
                className={classes.imageCover}
                loading="lazy"
                decoding="async"
            />
        </div>
    )
}
